// РУКА 2. Место под фото. Есть снимок (photos[0], грузит магазин через админку) — показываем его.
// Нет — значок раздела в стиле flat long-shadow: белый предмет и длинная тень под 45°.
//
// Правило тени: предмет отбрасывает её как единое целое — только с дальней (нижне-правой) стороны.
// Поэтому: 1) тень = «протяжка» частей предмета вниз-вправо; 2) поверх неё цветом плашки
// закрашивается всё, у чего дальше по диагонали ещё есть предмет (карманы между частями),
// — так тень никогда не ложится на освещённые края. Все видимые края тени — строго 45°.
// У значков с флагом solid этого высветления нет: прорези, выемки и дырки внутри предмета
// (батарея, ключ, маховик) лежат в его тени и закрашены цветом тени — как в жизни.
// 3) Белый предмет рисуется с обводкой 0,25: она перекрывает совпадающие границы тени и заливки,
// иначе сглаживание даёт по краю тёмную кайму или светлую щель.
import type { ReactNode } from "react";

type Pt = [number, number];

const L = 100; // длина тени — с запасом за край плашки

function arc(cx: number, cy: number, rx: number, ry: number, a0: number, a1: number, n: number): Pt[] {
  const o: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const a = ((a0 + ((a1 - a0) * i) / n) * Math.PI) / 180;
    o.push([cx + rx * Math.cos(a), cy + ry * Math.sin(a)]);
  }
  return o;
}
// Скруглённый прямоугольник с точными дугами (как <rect rx>).
function rr(x: number, y: number, w: number, h: number, r: number): Pt[] {
  if (r <= 0) return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
  const q = Math.min(r, w / 2, h / 2);
  return [
    ...arc(x + q, y + q, q, q, 180, 270, 8),
    ...arc(x + w - q, y + q, q, q, 270, 360, 8),
    ...arc(x + w - q, y + h - q, q, q, 0, 90, 8),
    ...arc(x + q, y + h - q, q, q, 90, 180, 8),
  ];
}
function circ(cx: number, cy: number, r: number): Pt[] {
  return arc(cx, cy, r, r, 0, 360, 64).slice(0, 64);
}
function sector(cx: number, cy: number, r: number, a0: number, a1: number): Pt[] {
  return [[cx, cy], ...arc(cx, cy, r, r, a0, a1, 16)];
}
function rotate(pts: Pt[], deg: number, ox: number, oy: number): Pt[] {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  return pts.map(([x, y]): Pt => [ox + (x - ox) * c - (y - oy) * s, oy + (x - ox) * s + (y - oy) * c]);
}
function hull(pts: Pt[]): Pt[] {
  const p = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cr = (o: Pt, a: Pt, b: Pt) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo: Pt[] = [];
  for (const q of p) {
    while (lo.length >= 2 && cr(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop();
    lo.push(q);
  }
  const up: Pt[] = [];
  for (let i = p.length - 1; i >= 0; i--) {
    const q = p[i];
    while (up.length >= 2 && cr(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop();
    up.push(q);
  }
  lo.pop();
  up.pop();
  return lo.concat(up);
}
// Протяжка выпуклых частей по диагонали от t0 до t1 — одним контуром (без швов между частями).
function sweep(parts: Pt[][], t0: number, t1: number): string {
  return parts
    .map((p) => {
      const h = hull([...p.map(([x, y]): Pt => [x + t0, y + t0]), ...p.map(([x, y]): Pt => [x + t1, y + t1])]);
      return "M" + h.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join("L") + "Z";
    })
    .join("");
}

// Батарея: 6 высоких секций по 8,5 — ширина 51, высота как у капли и термометра, по центру плашки.
const RAD_W = 8.5;
const RAD_X = Array.from({ length: 6 }, (_, i) => +(24.5 + RAD_W * i).toFixed(1));


// src — выпуклые части, целиком лежащие внутри белого силуэта (из них строится тень).
// solid — тень сплошная: карманы между частями не высветляются (тень заполняет их целиком).
// soft — в карманах между частями своя, более светлая тень (вторая тень, не сливается с основной).
type Cat = { tile: string; shadow: string; src: Pt[][]; display: ReactNode; neg?: ReactNode; solid?: boolean; soft?: string };

function cat(id: string): Cat {
  switch (id) {
    case "voda":
      return {
        tile: "#4C86C6",
        shadow: "#3268A5",
        src: [circ(50, 58, 20), [[50, 19], [37, 52], [63, 52]]],
        display: <path d="M50 19C50 19 30 44 30 58a20 20 0 0 0 40 0C70 44 50 19 50 19Z" />,
      };
    case "otoplenie":
      // секционная батарея, вытянутая: 9 секций со скруглённым верхом, ровный низ, прорези между секциями
      return {
        tile: "#C47A52",
        shadow: "#9F5C38",
        solid: true,
        src: [...RAD_X.map((x) => rr(x, 22, RAD_W, 50, RAD_W / 2)), rr(24.5, 30, 6 * RAD_W, 48, 3)],
        display: (
          <>
            {RAD_X.map((x) => (
              <rect key={x} x={x} y="22" width={RAD_W} height="50" rx={RAD_W / 2} />
            ))}
            <rect x="24.5" y="30" width={6 * RAD_W} height="48" rx="3" />
          </>
        ),
        neg: (
          <>
            {RAD_X.slice(1).map((x) => (
              <rect key={x} x={x - 0.7} y="30" width="1.4" height="40" rx="0.7" />
            ))}
          </>
        ),
      };
    case "termoreg":
      return {
        tile: "#45A193",
        shadow: "#327E73",
        // «плечи» под 45° там, где трубка входит в колбу, — тень трубки не задевает верх колбы
        src: [
          rr(44.5, 20, 11, 48, 5.5),
          circ(50, 71, 11),
          [[55.5, 60.94], [57.78, 63.22], [55.5, 61.47]],
          [[44.5, 60.94], [44.5, 61.47], [42.22, 63.22]],
        ],
        display: (
          <>
            <rect x="44.5" y="20" width="11" height="48" rx="5.5" />
            <circle cx="50" cy="71" r="11" />
            <polygon points="55.5,60.94 57.78,63.22 55.5,61.47" />
            <polygon points="44.5,60.94 44.5,61.47 42.22,63.22" />
          </>
        ),
      };
    case "truby":
      // колено с фланцами-гайками на обоих концах (скосы 45° — тень стекает по ним без ступенек)
      return {
        tile: "#7D8A97",
        soft: "#6E7B87",
        shadow: "#5F6B77",
        src: [
          rr(31, 28, 28, 14, 0),
          sector(59, 42, 14, 270, 360),
          rr(59, 42, 14, 30, 0),
          [[26, 25], [30, 25], [33, 28], [33, 42], [30, 45], [26, 45]],
          [[59, 70], [73, 70], [76, 73], [76, 76], [56, 76], [56, 73]],
        ],
        display: (
          <>
            <path d="M31 28L59 28A14 14 0 0 1 73 42L73 72L59 72L59 46A4 4 0 0 0 55 42L31 42Z" />
            <polygon points="26,25 30,25 33,28 33,42 30,45 26,45" />
            <polygon points="59,70 73,70 76,73 76,76 56,76 56,73" />
          </>
        ),
      };
    case "armatura":
      return {
        tile: "#3E64A9",
        shadow: "#2D4C87",
        solid: true,
        // корпус и патрубки с фасками 45° (вентиль «под гайку»)
        src: [
          [[24, 59], [40, 59], [40, 69], [24, 69], [22, 67], [22, 61]],
          [[60, 59], [76, 59], [78, 61], [78, 67], [76, 69], [60, 69]],
          [[40, 54], [60, 54], [62, 56], [62, 70], [60, 72], [40, 72], [38, 70], [38, 56]],
          rr(48, 40, 4, 16, 0),
          circ(50, 36, 10),
        ],
        display: (
          <>
            <polygon points="24,59 40,59 40,69 24,69 22,67 22,61" />
            <polygon points="60,59 76,59 78,61 78,67 76,69 60,69" />
            <polygon points="40,54 60,54 62,56 62,70 60,72 40,72 38,70 38,56" />
            <rect x="48" y="40" width="4" height="16" />
            <circle cx="50" cy="36" r="10" />
          </>
        ),
        neg: <circle cx="50" cy="36" r="4" />,
      };
    case "kanal":
      // косой тройник канализации: прямая труба и вторая труба под 45°, раструбы на концах; тень сплошная
      return {
        tile: "#9A8F83",
        shadow: "#786E63",
        solid: true,
        src: [
          rr(20, 56, 58, 14, 2),
          rr(14, 53, 12, 20, 2.5),
          rr(70, 53, 12, 20, 2.5),
          rotate(rr(46, 56, 34, 14, 1), -45, 46, 63),
          rotate(rr(74, 53, 12, 20, 2.5), -45, 46, 63),
        ],
        display: (
          <>
            <rect x="20" y="56" width="58" height="14" rx="2" />
            <rect x="14" y="53" width="12" height="20" rx="2.5" />
            <rect x="70" y="53" width="12" height="20" rx="2.5" />
            <g transform="rotate(-45 46 63)">
              <rect x="46" y="56" width="34" height="14" rx="1" />
              <rect x="74" y="53" width="12" height="20" rx="2.5" />
            </g>
          </>
        ),
      };
    case "izol":
      return {
        tile: "#7172B5",
        shadow: "#555694",
        solid: true,
        // рожковый ключ: зев с параллельными губками; головка = низ + две губки (все части выпуклые)
        src: [
          rotate(rr(45.5, 40, 9, 36, 4.5), 42, 50, 50),
          rotate(arc(50, 34, 11, 11, -5.22, 185.22, 24), 42, 50, 50),
          rotate([...arc(50, 34, 11, 11, 185.22, 248.67, 8), [46, 33]], 42, 50, 50),
          rotate([[54, 33], ...arc(50, 34, 11, 11, 291.33, 354.78, 8)], 42, 50, 50),
        ],
        display: (
          <g transform="rotate(42 50 50)">
            <rect x="45.5" y="40" width="9" height="36" rx="4.5" />
            <path d="M54 23.75A11 11 0 1 1 46 23.75L46 33L54 33Z" />
          </g>
        ),
      };
    default:
      return {
        tile: "#8A97A3",
        shadow: "#697680",
        src: [rr(30, 26, 40, 40, 6)],
        display: <rect x="30" y="26" width="40" height="40" rx="6" />,
      };
  }
}

const geomCache = new Map<string, { down: string; up: string }>();
function geom(id: string, src: Pt[][], solid?: boolean) {
  let g = geomCache.get(id);
  if (!g) {
    g = { down: sweep(src, 0, L), up: solid ? "" : sweep(src, -L, 0) };
    geomCache.set(id, g);
  }
  return g;
}

export function PhotoSlot({
  src,
  alt,
  categoryId,
  large = false,
}: {
  src?: string;
  alt: string;
  categoryId?: string | null;
  large?: boolean;
}): ReactNode {
  if (src) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={src} alt={alt} className="w-full h-full object-cover" />;
  }
  const id = categoryId ?? "";
  const c = cat(id);
  const g = geom(id, c.src, c.solid);
  return (
    <svg width="100%" height="100%" viewBox="0 0 100 100" role="img" aria-label={alt} data-size={large ? "lg" : "sm"}>
      <rect x="0" y="0" width="100" height="100" fill={c.tile} />
      {c.soft && g.up ? (
        <>
          {/* светлая тень везде, тёмная — только там, где за ней по диагонали нет предмета */}
          <defs>
            <mask id={`soft-${id}`} maskUnits="userSpaceOnUse" x="-200" y="-200" width="500" height="500">
              <rect x="-200" y="-200" width="500" height="500" fill="#fff" />
              <path d={g.up} fill="#000" />
            </mask>
          </defs>
          <path d={g.down} fill={c.soft} />
          <path d={g.down} fill={c.shadow} mask={`url(#soft-${id})`} />
        </>
      ) : (
        <>
          <path d={g.down} fill={c.shadow} />
          {g.up ? <path d={g.up} fill={c.tile} /> : null}
        </>
      )}
      <g fill="#ffffff" stroke="#ffffff" strokeWidth={0.5}>
        {c.display}
      </g>
      {/* дырки внутри предмета закрыты им со всех сторон — в них лежит тень */}
      {c.neg ? <g fill={c.shadow}>{c.neg}</g> : null}
    </svg>
  );
}
